import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useDatabase } from '../contexts/DatabaseContext';
import { apiClient } from '../services/apiClient';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import { debounce } from 'lodash';

// Типы для статусов
type EditorStatus = 'loading' | 'ready' | 'error';
type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'syncing' | 'synced' | 'error';

/**
 * Свойства компонента TiptapWithRxDB
 */
interface TiptapWithRxDBProps {
  documentId: string;
  className?: string;
  autoSaveDelay?: number;
  syncDelay?: number;
  placeholder?: string;
}

/**
 * Компонент Tiptap с интеграцией RxDB для автосохранения
 */
export const TiptapWithRxDB: React.FC<TiptapWithRxDBProps> = ({
  documentId,
  className = '',
  autoSaveDelay = 300,
  syncDelay = 2000
}) => {
  // Доступ к базе данных
  const { repository, isInitialized } = useDatabase();
  
  // Состояния
  const [editorStatus, setEditorStatus] = useState<EditorStatus>('loading');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [editorContent, setEditorContent] = useState(null);
  const [error, setError] = useState<string | null>(null);
  
  // Refs для предыдущего контента и таймеров
  const lastSavedContent = useRef<any>(null);
  const serverSyncTimerRef = useRef<number | null>(null);
  
  // Инициализация редактора Tiptap
  const editor = useEditor({
    extensions: [
      StarterKit
    ],
    content: editorContent,
    onUpdate: ({ editor }) => {
      const content = editor.getJSON();
      debouncedSave(content);
    },
    editable: editorStatus === 'ready'
  });

  // Функция загрузки документа
  const loadDocument = useCallback(async () => {
    if (!documentId || !isInitialized) return;
    
    setEditorStatus('loading');
    setError(null);
    
    try {
      // Пытаемся загрузить документ из локальной базы данных
      const localDoc = await repository.getDocumentById(documentId);
      
      if (localDoc) {
        // Документ найден в локальной БД - используем его
        setEditorContent(localDoc.content);
        lastSavedContent.current = localDoc.content;
        setEditorStatus('ready');
        setSaveStatus(localDoc.syncStatus === 'synced' ? 'synced' : 'saved');
        
        // Асинхронно проверяем актуальность с сервером
        try {
          const serverData = await apiClient.fetchDocument(documentId);
          
          // Проверяем, нужно ли обновить локальную версию
          const isServerNewer = serverData.serverTimestamp > localDoc.serverTimestamp;
          const isLocalSynced = localDoc.syncStatus === 'synced';
          
          if (isServerNewer && isLocalSynced) {
            // Обновляем данные в RxDB и редакторе
            await repository.saveDocument({
              id: documentId,
              content: serverData.content,
              serverTimestamp: serverData.serverTimestamp,
              syncStatus: 'synced'
            });
            
            setEditorContent(serverData.content);
            lastSavedContent.current = serverData.content;
          }
        } catch (serverError) {
          console.error('Ошибка при проверке актуальности документа:', serverError);
          // Не меняем статус, т.к. локальная версия уже загружена
        }
      } else {
        // Документ не найден локально - загружаем с сервера
        try {
          const serverData = await apiClient.fetchDocument(documentId);
          
          // Сохраняем в локальную БД
          await repository.saveDocument({
            id: documentId,
            content: serverData.content,
            serverTimestamp: serverData.serverTimestamp,
            syncStatus: 'synced'
          });
          
          setEditorContent(serverData.content);
          lastSavedContent.current = serverData.content;
          setEditorStatus('ready');
          setSaveStatus('synced');
        } catch (serverError) {
          setError('Не удалось загрузить документ. Попробуйте позже.');
          setEditorStatus('error');
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке документа:', error);
      setError('Ошибка при загрузке документа');
      setEditorStatus('error');
    }
  }, [documentId, repository, isInitialized]);
  
  // Функция сохранения в RxDB с debounce
  const saveToDatabase = useCallback(async (content: any) => {
    if (!documentId || !isInitialized) return;
    
    // Если контент не изменился, не сохраняем
    if (JSON.stringify(content) === JSON.stringify(lastSavedContent.current)) {
      return;
    }
    
    setSaveStatus('saving');
    
    try {
      // Сохраняем в локальную БД
      const savedDoc = await repository.saveDocument({
        id: documentId,
        content
      });
      
      lastSavedContent.current = content;
      setSaveStatus('saved');
      
      // Запускаем синхронизацию с сервером с задержкой
      if (serverSyncTimerRef.current) {
        window.clearTimeout(serverSyncTimerRef.current);
      }
      
      serverSyncTimerRef.current = window.setTimeout(() => {
        syncWithServer();
      }, syncDelay);
      
    } catch (error) {
      console.error('Ошибка при сохранении в БД:', error);
      setSaveStatus('error');
    }
  }, [documentId, repository, isInitialized, syncDelay]);
  
  // Создаем debounced версию функции сохранения
  const debouncedSave = useCallback(
    debounce((content: any) => {
      saveToDatabase(content);
    }, autoSaveDelay),
    [saveToDatabase, autoSaveDelay]
  );
  
  // Функция синхронизации с сервером
  const syncWithServer = useCallback(async () => {
    if (!documentId || !isInitialized) return;
    
    // Получаем последнюю версию документа из БД
    const doc = await repository.getDocumentById(documentId);
    
    if (!doc || doc.syncStatus === 'synced') {
      return;
    }
    
    setSaveStatus('syncing');
    
    try {
      // Отправляем на сервер
      const result = await apiClient.saveDocument(documentId, doc.content);
      
      // Обновляем статус в БД
      await repository.updateSyncStatus(
        documentId,
        'synced',
        result.serverTimestamp
      );
      
      setSaveStatus('synced');
      
      // Возвращаем статус к "saved" через 2 секунды
      setTimeout(() => {
        setSaveStatus('saved');
      }, 2000);
      
    } catch (error) {
      console.error('Ошибка при синхронизации с сервером:', error);
      setSaveStatus('error');
    }
  }, [documentId, repository, isInitialized]);
  
  // Функция для ручной синхронизации
  const forceSaveAndSync = useCallback(async () => {
    if (!editor || !documentId) return;
    
    // Отменяем текущие таймеры
    debouncedSave.cancel();
    if (serverSyncTimerRef.current) {
      window.clearTimeout(serverSyncTimerRef.current);
      serverSyncTimerRef.current = null;
    }
    
    const content = editor.getJSON();
    
    // Сохраняем в БД и синхронизируем с сервером
    await saveToDatabase(content);
    await syncWithServer();
    
  }, [editor, documentId, debouncedSave, saveToDatabase, syncWithServer]);
  
  // Загружаем документ при монтировании или изменении ID
  useEffect(() => {
    if (isInitialized) {
      loadDocument();
    }
  }, [loadDocument, isInitialized]);
  
  // Синхронизация с сервером при размонтировании
  useEffect(() => {
    return () => {
      // Отменяем все таймеры
      debouncedSave.cancel();
      
      if (serverSyncTimerRef.current) {
        window.clearTimeout(serverSyncTimerRef.current);
        serverSyncTimerRef.current = null;
      }
      
      // Если есть несинхронизированные изменения - синхронизируем
      const finalSync = async () => {
        if (!documentId || !isInitialized) return;
        
        const doc = await repository.getDocumentById(documentId);
        if (doc && doc.syncStatus === 'pending') {
          syncWithServer();
        }
      };
      
      finalSync();
    };
  }, [debouncedSave, documentId, isInitialized, repository, syncWithServer]);
  
  // Обновляем контент редактора при изменении editorContent
  useEffect(() => {
    if (editor && editorContent && !editor.isDestroyed) {
      // Проверяем, отличается ли текущий контент от нового
      const currentContent = editor.getJSON();
      
      if (JSON.stringify(currentContent) !== JSON.stringify(editorContent)) {
        editor.commands.setContent(editorContent);
      }
    }
  }, [editor, editorContent]);
  
  // Обработка горячих клавиш (Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        forceSaveAndSync();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [forceSaveAndSync]);
  
  // Хук для обработки закрытия страницы
  useBeforeUnload();
  
  // Отображение статуса в UI
  const getStatusText = () => {
    switch (saveStatus) {
      case 'saving': return 'Сохранение...';
      case 'saved': return 'Сохранено локально2';
      case 'unsaved': return 'Несохраненные изменения';
      case 'syncing': return 'Синхронизация с сервером...';
      case 'synced': return 'Синхронизировано';
      case 'error': return 'Ошибка сохранения';
      default: return 'Актуально';
    }
  };
  
  const getStatusClass = () => {
    switch (saveStatus) {
      case 'saving':
      case 'unsaved':
        return 'text-amber-500';
      case 'syncing':
        return 'text-blue-500';
      case 'synced':
      case 'saved':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-green-500';
    }
  };
  
  // Отображаем состояние загрузки
  if (editorStatus === 'loading') {
    return <div className="p-4 text-center">Загрузка документа...</div>;
  }
  
  // Отображаем ошибку
  if (editorStatus === 'error') {
    return <div className="p-4 text-center text-red-500">{error || 'Ошибка загрузки документа'}</div>;
  }
  
  return (
    <div className={`relative border rounded-lg ${className}`}>
      {/* Статус сохранения */}
      <div className="absolute top-2 right-4 z-10">
        <span className={`text-sm font-medium ${getStatusClass()}`}>
          {getStatusText()}
        </span>
      </div>
      
      {/* Редактор */}
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4"
      />
      
      {/* Кнопка принудительной синхронизации */}
      <div className="absolute bottom-2 right-4">
        <button
          className="px-3 py-1 bg-blue-500 text-white rounded text-sm"
          onClick={forceSaveAndSync}
          disabled={saveStatus === 'syncing'}
        >
          Сохранить
        </button>
      </div>
    </div>
  );
}; 